<template>
  <transition name="welcome-message">
    <div class="welcome-message" v-if="showWelcome">
      <div class="welcome-message-container">
        <header class="welcome-message-header">
          <span class="welcome-message-header-title">{{ $t('commonText.welcome') }}</span>
          <product-logo class="welcome-message-logo" color="red" />
        </header>

        <div class="welcome-message-content">
          <div class="welcome-message-slider" :style="sliderStyles">
            <div class="welcome-message-video" v-for="({ url, title }, index) in tips" :key="index">
              <strong class="welcome-message-video-title">{{ title }}</strong>
              <div class="welcome-message-video-media">
                <iframe frameborder="0" allowfullscreen :src="url" v-if="sliderActive === index"></iframe>
              </div>
            </div>
          </div>
        </div>

        <div class="welcome-message-actions">
          <transition name="welcome-message-action">
            <button class="welcome-message-action welcome-message-previous" @click="previousVideo" v-if="sliderActive > 0">
              <svg-loader src="/assets/images/icon-arrow-back.svg" />
            </button>
          </transition>

          <transition name="welcome-message-action">
            <button class="welcome-message-action welcome-message-next" @click="nextVideo" v-if="sliderActive < tips.length - 1">
              <svg-loader src="/assets/images/icon-arrow-forward.svg" />
            </button>
          </transition>

          <transition name="welcome-message-action">
            <button class="welcome-message-action welcome-message-done" @click="skipDialog" v-if="isWelcomeDone">
              <svg-loader src="/assets/images/icon-done.svg" />
            </button>
          </transition>
        </div>

        <footer class="welcome-message-footer">
          <button class="welcome-message-skip" @click="skipDialog">{{ skipText }}</button>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'

  export default {
    name: 'WelcomeMessage',
    data: () => ({
      sliderActive: 0,
      showWelcome: false
    }),
    computed: {
      ...mapGetters('auth', ['userId']),
      ...mapState('userNotifications', ['tips']),
      sliderStyles () {
        return `transform: translate3D(-${640 * this.sliderActive}px, 0, 0)`
      },
      updateStore () {
        return this.$firebase.collection('updates')
      },
      isWelcomeDone () {
        return this.sliderActive === this.tips.length - 1
      },
      skipText () {
        if (this.isWelcomeDone) {
          return this.$t('commonText.close')
        }

        return this.$t('commonText.skipVideo')
      }
    },
    watch: {
      userId: {
        immediate: true,
        handler (value) {
          if (value) {
            this.startWelcome()
          }
        }
      }
    },
    methods: {
      hasReadWelcome () {
        return new Promise(resolve => {
          this.updateStore
            .doc(this.userId.toString())
            .get()
            .then(doc => {
              if (doc.exists) {
                resolve(Boolean(doc.data().welcome))
              }

              resolve(false)
            })
        })
      },
      skipDialog () {
        this.updateStore.doc(this.userId.toString()).set({
          welcome: true
        }, { merge: true })
        this.showWelcome = false

        if (this.isWelcomeDone) {
          this.$analytics('send', {
            hitType: 'event',
            eventCategory: 'Onboard',
            eventAction: 'done-welcome'
          })
        } else {
          this.$analytics('send', {
            hitType: 'event',
            eventCategory: 'Onboard',
            eventAction: 'skip-welcome',
            eventLabel: `Welcome Video ${this.sliderActive + 1}`
          })
        }
      },
      previousVideo () {
        this.sliderActive--
        this.trackVideoViews()
      },
      nextVideo () {
        this.sliderActive++
        this.trackVideoViews()
      },
      trackVideoViews () {
        this.$analytics('send', {
          hitType: 'event',
          eventCategory: 'Onboard',
          eventAction: 'view-welcome-video',
          eventLabel: `Welcome Video ${this.sliderActive + 1}`
        })
      },
      startWelcome () {
        this.hasReadWelcome().then(hasReadWelcome => {
          this.showWelcome = !hasReadWelcome && this.$i18n.locale.includes('pt')

          if (this.showWelcome) {
            this.trackVideoViews()
          }
        })
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import '~stylesheets/meta';

  .welcome-message {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 130;
    background-color: rgba(#fff, .85);
    transition: .3s $transition-ease-out;
  }

  .welcome-message-enter,
  .welcome-message-leave-active {
    opacity: 0;

    .welcome-message-container {
      transform: scale(.87);
      box-shadow: 0;
    }
  }

  .welcome-message-container {
    max-width: 736px;
    padding: 26px 48px;
    position: relative;
    background-color: #fff;
    border-radius: 10px;
    box-shadow: 0 4px 34px 0 rgba($color-dark-grey, .38);
    transition: .3s $transition-ease-out;
  }

  .welcome-message-header {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .welcome-message-header-title {
    margin-right: 12px;
    color: $color-marine;
    font-size: 25px;
    font-style: italic;
    line-height: 34px;
  }

  .welcome-message-logo {
    transform: scale(1.1);
    transform-origin: left center;
  }

  .welcome-message-content {
    margin-top: 36px;
    overflow: hidden;
  }

  .welcome-message-slider {
    display: flex;
    transition: .3s $transition-ease-out;
  }

  .welcome-message-video {
    flex: 0 0 100%;
    text-align: center;
  }

  .welcome-message-video-media {
    width: 640px;
    height: 350px;
    margin-top: 8px;

    iframe {
      width: 100%;
      height: 100%;
    }
  }

  .welcome-message-video-title {
    font-size: 15px;
    font-weight: 600;
    line-height: 1.5em;
  }

  .welcome-message-action {
    @include clear-button;
    width: 48px;
    height: 48px;
    position: absolute;
    top: 50%;
    background: #fff;
    border-radius: 50%;
    box-shadow: 0 2px 12px 0 rgba($color-dark-grey, .26);

    &:hover:not(.welcome-message-done) {
      background: #fafafa;
      box-shadow: 0 5px 20px 0 rgba($color-dark-grey, .38);

      svg {
        fill: rgba(#000, .87);
      }
    }

    svg {
      width: 24px;
      height: 24px;
      fill: rgba(#000, .54);
      transition: .3s $transition-ease-out;
    }
  }

  .welcome-message-previous {
    left: -72px;
  }

  .welcome-message-next,
  .welcome-message-done {
    right: -72px;
  }

  .welcome-message-done svg {
    fill: $color-blue;
  }

  .welcome-message-action-enter,
  .welcome-message-action-leave-active {
    opacity: 0;
    transform: scale(0);
  }

  .welcome-message-footer {
    margin-top: 26px;
    text-align: right;
  }

  .welcome-message-skip {
    @include clear-button;
    color: $color-cool-grey;
    font-size: 16px;
    font-style: italic;
    line-height: 22px;

    &:hover {
      color: $color-darker-grey;
    }
  }
</style>
